import useCustomLazyQuery from '@hooks/useCustomLazyQuery'
import { StoreProps } from '@interfaces/StoreState'
import { getLocale } from '@layouts/PortalTranslations/utils'
import { GET_TRANSLATIONS } from '@queries/translations'
import { updateLocale } from '@redux/actions/intl'
import { PROFILE_TYPE } from '@utils/constants'
import isEqual from 'lodash/isEqual'
import React, { useEffect, useRef } from 'react'
import { IntlProvider } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { getStorageAdapter, AUTH_KEYS } from '@shell/adapters/storage'

const REQUIRED_CERTIFICATE_TRANSLATION_KEYS = [
  'AI Literacy & Compliance Training',
  'Certificate of Completion',
  'Completed the following program',
  'Completion Date',
  'Congratulations! View Certificate',
]

const hasMessages = (messages?: Record<string, string>) => Boolean(Object.keys(messages || {}).length)
const hasRequiredCertificateMessages = (messages?: Record<string, string>) =>
  REQUIRED_CERTIFICATE_TRANSLATION_KEYS.every((key) => Boolean(messages?.[key]))

const UserTranslations: React.FC<{ defaultLang?: string; children: React.ReactNode }> = ({ children, defaultLang }) => {
  const dispatch = useDispatch()
  const { lang, storedMessages } = useSelector(({ userProfile, intl }: StoreProps) => ({
    lang:
      userProfile?.info?.Type === PROFILE_TYPE.STUDENT
        ? userProfile?.info?.StudentProfile?.StudentProfileInfo?.PortalLanguage
        : userProfile?.info?.InstructorProfile?.instructorInfo?.PortalLanguage,
    storedMessages: intl.messages,
  }))

  const [getTranslations, { data: translations }] = useCustomLazyQuery(GET_TRANSLATIONS)
  const initialNow = Date.now()
  const lastRequestedLocale = useRef<string>()

  useEffect(() => {
    try {
      const messages = JSON.parse(translations?.localization?.translations || null)
      if (messages !== '' && !isEqual(messages, storedMessages)) {
        dispatch(updateLocale({ locale: getLocale(lang || defaultLang), messages }))
      }
    } catch (e) {
      console.error('Translation Error', e)
    }
  }, [translations])

  useEffect(() => {
    const locale = lang || defaultLang
    const loadedLang = getStorageAdapter().get(AUTH_KEYS.LOADED_LOCALE)

    if (!locale) return

    const normalizedLocale = getLocale(locale)
    const hasCurrentMessages = hasMessages(storedMessages)
    const hasScormCertificateMessages = hasRequiredCertificateMessages(storedMessages)

    if (loadedLang === normalizedLocale && hasCurrentMessages && hasScormCertificateMessages) return
    if (lastRequestedLocale.current === normalizedLocale && hasCurrentMessages) return

    lastRequestedLocale.current = normalizedLocale
    getStorageAdapter().set(AUTH_KEYS.LOADED_LOCALE, normalizedLocale)
    getStorageAdapter().set(AUTH_KEYS.APP_LOCALE, locale)
    getTranslations({ locale: normalizedLocale })
  }, [lang, storedMessages])

  return (
    <>
      <IntlProvider
        locale={getLocale(lang)}
        key={getLocale(lang)}
        messages={storedMessages}
        initialNow={initialNow}
        onError={(err) => {
          // @ts-ignore
          if (err.code === 'MISSING_TRANSLATION') {
            // @ts-ignore
            __DEV__ && console.warn('Missing translation', err.message)
            return
          }
          throw err
        }}
      >
        {children}
      </IntlProvider>
    </>
  )
}

export default UserTranslations
